import type { CSSProperties, ReactNode, MouseEventHandler } from 'react'

export function Eyebrow({ children, light }: { children: ReactNode; light?: boolean }) {
  return (
    <div style={{
      fontFamily: 'var(--font-body)',
      fontSize: 11, fontWeight: 500,
      letterSpacing: '0.24em', textTransform: 'uppercase',
      color: light ? 'var(--fg-inverse-muted)' : 'var(--fg-soft)',
    }}>{children}</div>
  )
}

export function Bar({ light, tall }: { light?: boolean; tall?: boolean }) {
  return (
    <div style={{
      width: 3, alignSelf: 'stretch',
      background: light ? 'var(--fg-inverse)' : 'var(--fg)',
      margin: tall ? '4px 0' : '2px 0',
      flexShrink: 0,
    }} />
  )
}

export function Hand({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <span style={{ fontFamily: 'var(--font-script)', fontWeight: 500, ...style }}>{children}</span>
  )
}

export function Quote({ children }: { children: ReactNode }) {
  return (
    <p style={{
      fontFamily: 'var(--font-serif)', fontStyle: 'italic',
      fontSize: 28, lineHeight: 1.22, letterSpacing: '-0.01em', margin: 0,
    }}>{children}</p>
  )
}

type ButtonVariant = 'primary' | 'ghost' | 'accent' | 'ghostLight'

export function Button({
  children, variant = 'primary', onClick, href, type,
}: {
  children: ReactNode
  variant?: ButtonVariant
  onClick?: MouseEventHandler
  href?: string
  type?: 'button' | 'submit' | 'reset'
}) {
  const base: CSSProperties = {
    fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 13,
    letterSpacing: '0.12em', textTransform: 'uppercase',
    padding: '14px 26px', border: 'none', cursor: 'pointer',
    textDecoration: 'none', display: 'inline-block',
    transition: 'opacity 140ms var(--ease-smooth), background 140ms, color 140ms',
  }
  const variants: Record<ButtonVariant, CSSProperties> = {
    primary:    { background: 'var(--ink)',     color: 'var(--bone)' },
    ghost:      { background: 'transparent',    color: 'var(--fg)',          border: '1px solid var(--fg)' },
    accent:     { background: 'var(--accent)',  color: 'var(--pure)' },
    ghostLight: { background: 'transparent',    color: 'var(--fg-inverse)',  border: '1px solid var(--fg-inverse)' },
  }
  const style: CSSProperties = { ...base, ...variants[variant] }
  if (href) {
    return (
      <a href={href} onClick={onClick} style={style}
         onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.72' }}
         onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}>
        {children}
      </a>
    )
  }
  return (
    <button type={type ?? 'button'} onClick={onClick} style={style}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.72' }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}>
      {children}
    </button>
  )
}

export function TitleBar({
  children, size = 48, light,
}: { children: ReactNode; size?: number; light?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
      <Bar light={light} />
      <h1 className="ax-title-bar" style={{
        fontFamily: 'var(--font-display)', fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.04em',
        fontSize: size, lineHeight: 1.02, margin: 0,
        color: light ? 'var(--fg-inverse)' : 'var(--fg)',
      }}>{children}</h1>
    </div>
  )
}
